import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input, Select, Modal, Dropdown, Spin } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import dayjs from "dayjs";
import type {
  LibraryReference,
  ReferenceCategory,
  GlobalFramework,
  FrameworkIndicator,
  BenchmarkValueContent,
  IndicatorFrameworkRow,
  ImportedGroup,
} from "./library.service";
import {
  listReferences,
  createReference,
  updateReference,
  deleteReference,
  listFrameworks,
  getFrameworkDetail,
  importIndicators,
  listImported,
  unimportReferences,
} from "./library.service";
import "./LibraryPage.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABEL_KEYS: Record<ReferenceCategory, string> = {
  benchmark_value: "categories.benchmarkValue",
  lookup_table: "categories.lookupTable",
  indicator_framework: "categories.indicatorFramework",
  classification: "categories.classification",
  other: "categories.other",
};

const CATEGORY_COLORS: Record<ReferenceCategory, string> = {
  benchmark_value: "#5540af",
  lookup_table: "#0891b2",
  indicator_framework: "#16a34a",
  classification: "#d97706",
  other: "#6b7280",
};

const OPERATOR_SYMBOLS: Record<string, string> = {
  gte: "≥",
  lte: "≤",
  eq: "=",
  gt: ">",
  lt: "<",
};

const UNIT_OPTIONS = [
  { value: "%", labelKey: "" },
  { value: "score", labelKey: "units.score" },
  { value: "count", labelKey: "units.count" },
  { value: "index", labelKey: "units.index" },
];

const OPERATOR_OPTIONS = [
  { value: "", labelKey: "operators.none" },
  { value: "gte", labelKey: "operators.gte" },
  { value: "lte", labelKey: "operators.lte" },
  { value: "eq", labelKey: "operators.eq" },
  { value: "gt", labelKey: "operators.gt" },
  { value: "lt", labelKey: "operators.lt" },
];

const ISSUING_ABBR: Record<string, string> = {
  "United Nations": "UN",
  "World Health Organization": "WH",
  OECD: "OE",
  "World Bank": "WB",
  "Sphere Project": "SP",
};

const CATEGORY_DEFS: {
  value: ReferenceCategory;
  labelKey: string;
  descKey: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "benchmark_value",
    labelKey: "categoryDefs.benchmarkValue.label",
    descKey: "categoryDefs.benchmarkValue.desc",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" />
        <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" />
        <line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
      </svg>
    ),
  },
  {
    value: "lookup_table",
    labelKey: "categoryDefs.lookupTable.label",
    descKey: "categoryDefs.lookupTable.desc",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
        <line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    ),
  },
  {
    value: "indicator_framework",
    labelKey: "categoryDefs.indicatorFramework.label",
    descKey: "categoryDefs.indicatorFramework.desc",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
      </svg>
    ),
  },
  {
    value: "classification",
    labelKey: "categoryDefs.classification.label",
    descKey: "categoryDefs.classification.desc",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
];

// ── Modal form state ──────────────────────────────────────────────────────────

interface ModalState {
  open: boolean;
  mode: "add" | "edit";
  id?: string;
  title: string;
  category: ReferenceCategory;
  description: string;
  citation: string;
  date: string;
  bv_value: string;
  bv_unit: string;
  bv_operator: string;
  bv_source_text: string;
  bv_source_url: string;
  lt_file_name: string;
  lt_file: File | null;
  if_rows: IndicatorFrameworkRow[];
  co_text: string;
  co_mode: "text" | "file";
  co_file_name: string;
  co_file: File | null;
}

const MODAL_DEFAULT: ModalState = {
  open: false,
  mode: "add",
  title: "",
  category: "benchmark_value",
  description: "",
  citation: "",
  date: dayjs().format("YYYY-MM-DD"),
  bv_value: "",
  bv_unit: "%",
  bv_operator: "gte",
  bv_source_text: "",
  bv_source_url: "",
  lt_file_name: "",
  lt_file: null,
  if_rows: [{ name: "", description: "", default_value: "" }],
  co_text: "",
  co_mode: "text",
  co_file_name: "",
  co_file: null,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildReferenceValueJSON(d: ModalState): any {
  switch (d.category) {
    case "benchmark_value":
      return {
        value: parseFloat(d.bv_value) || 0,
        unit: d.bv_unit,
        operator: d.bv_operator || undefined,
        source_text: d.bv_source_text || undefined,
        source_url: d.bv_source_url || undefined,
      };
    case "indicator_framework":
      return {
        rows: d.if_rows
          .filter((r) => r.name.trim())
          .map((r) => ({
            indicator_name: r.name,
            description: r.description || undefined,
            default_value: r.default_value ? parseFloat(r.default_value) : undefined,
          })),
      };
    case "classification":
    case "other":
      if (d.co_mode === "text") return { text: d.co_text };
      return null;
    default:
      return null;
  }
}

function isModalValid(d: ModalState): boolean {
  if (!d.title.trim()) return false;
  if (d.category === "benchmark_value" && !d.bv_value.trim()) return false;
  if (d.category === "indicator_framework" && d.if_rows.every((r) => !r.name.trim())) return false;
  if (d.category === "lookup_table" && d.mode === "add" && !d.lt_file) return false;
  return true;
}

function getValueSummary(ref: LibraryReference, t: TFunction): string {
  if (ref.category === "benchmark_value" && ref.value) {
    const v = ref.value as BenchmarkValueContent;
    const op = v.operator ? (OPERATOR_SYMBOLS[v.operator] ?? "") + " " : "";
    return `${op}${v.value}${v.unit ? " " + v.unit : ""}`;
  }
  if (ref.category === "lookup_table") return ref.value ? t("referenceLibrary.valueSummary.file") : "—";
  if (ref.category === "indicator_framework" && ref.value) {
    const rows = Array.isArray(ref.value) ? ref.value : (ref.value?.rows ?? []);
    const count = rows.length;
    return t("referenceLibrary.valueSummary.indicatorCount", { count });
  }
  return "";
}

// ── Component ─────────────────────────────────────────────────────────────────

const LibraryPage: React.FC = () => {
  const { t } = useTranslation("library");
  const { id: projectId } = useParams<{ id: string }>();

  const categoryLabels: Record<ReferenceCategory, string> = {
    benchmark_value: t(CATEGORY_LABEL_KEYS.benchmark_value),
    lookup_table: t(CATEGORY_LABEL_KEYS.lookup_table),
    indicator_framework: t(CATEGORY_LABEL_KEYS.indicator_framework),
    classification: t(CATEGORY_LABEL_KEYS.classification),
    other: t(CATEGORY_LABEL_KEYS.other),
  };

  const categoryDefs = CATEGORY_DEFS.map((d) => ({
    ...d,
    label: t(d.labelKey),
    desc: t(d.descKey),
  }));

  const unitOptions = UNIT_OPTIONS.map((o) => ({
    value: o.value,
    label: o.labelKey ? t(o.labelKey) : o.value,
  }));

  const operatorOptions = OPERATOR_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));

  // Tabs
  const [activeTab, setActiveTab] = useState<"my-references" | "global-standards">("my-references");
  const [myRefView, setMyRefView] = useState<"library" | "imported">("library");

  // My References
  const [references, setReferences] = useState<LibraryReference[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [refSearch, setRefSearch] = useState("");
  const [refCategoryFilter, setRefCategoryFilter] = useState<ReferenceCategory | "">("");
  const [modal, setModal] = useState<ModalState>(MODAL_DEFAULT);
  const [dirtyModal, setDirtyModal] = useState(false);
  const [savingModal, setSavingModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deletingRef, setDeletingRef] = useState(false);
  const ltFileInputRef = useRef<HTMLInputElement>(null);
  const coFileInputRef = useRef<HTMLInputElement>(null);

  // Global Standards
  const [frameworks, setFrameworks] = useState<GlobalFramework[]>([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [fwSearch, setFwSearch] = useState("");
  const [fwSourceFilter, setFwSourceFilter] = useState("");
  const [fwSectorFilter, setFwSectorFilter] = useState("");
  const [fwYearFilter, setFwYearFilter] = useState("");
  const [selectedFramework, setSelectedFramework] = useState<GlobalFramework | null>(null);
  const [fwIndicators, setFwIndicators] = useState<FrameworkIndicator[]>([]);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [fwIndicatorSearch, setFwIndicatorSearch] = useState("");
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<Set<string>>(new Set());
  const [importingIndicators, setImportingIndicators] = useState(false);

  // Imported
  const [importedGroups, setImportedGroups] = useState<ImportedGroup[]>([]);
  const [loadingImported, setLoadingImported] = useState(false);
  const [expandedFrameworks, setExpandedFrameworks] = useState<Set<string>>(new Set());

  // ── Data loading
  useEffect(() => {
    if (!projectId) return;
    setLoadingRefs(true);
    listReferences(projectId, { type: "user_created" })
      .then(({ items }) => setReferences(items))
      .catch(() => toast.error(t("toasts.loadReferencesFailed")))
      .finally(() => setLoadingRefs(false));
  }, [projectId]);

  useEffect(() => {
    if (!projectId || activeTab !== "global-standards") return;
    setLoadingFrameworks(true);
    listFrameworks({ project_id: projectId })
      .then(({ items }) => setFrameworks(items))
      .catch(() => toast.error(t("toasts.loadFrameworksFailed")))
      .finally(() => setLoadingFrameworks(false));
  }, [projectId, activeTab]);

  useEffect(() => {
    if (!projectId || myRefView !== "imported") return;
    setLoadingImported(true);
    listImported(projectId)
      .then(setImportedGroups)
      .catch(() => toast.error(t("toasts.loadImportedFailed")))
      .finally(() => setLoadingImported(false));
  }, [projectId, myRefView]);

  const refreshImported = async () => {
    if (!projectId) return;
    try {
      const groups = await listImported(projectId);
      setImportedGroups(groups);
    } catch {
      // silent refresh failure is acceptable
    }
  };

  // ── Filtered references
  const filteredRefs = references.filter((r) => {
    const matchesSearch =
      !refSearch ||
      r.title.toLowerCase().includes(refSearch.toLowerCase()) ||
      (r.description ?? "").toLowerCase().includes(refSearch.toLowerCase());
    return (!refCategoryFilter || r.category === refCategoryFilter) && matchesSearch;
  });

  // ── Derive filter options from actual data
  const availableCategories = references
    .map((r) => r.category)
    .filter((v, i, a) => a.indexOf(v) === i);

  // ── Filtered frameworks (client-side)
  const allIssuingBodies = frameworks.map((f) => f.issuing_body).filter((v, i, a) => a.indexOf(v) === i);
  const allSectors = frameworks.flatMap((f) => f.sector_tags ?? []).filter((v, i, a) => a.indexOf(v) === i);
  const allYears = frameworks
    .map((f) => String(new Date(f.last_updated).getFullYear()))
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
    .reverse();

  const filteredFrameworks = frameworks.filter((f) => {
    const matchesSearch =
      !fwSearch ||
      f.name.toLowerCase().includes(fwSearch.toLowerCase()) ||
      f.description.toLowerCase().includes(fwSearch.toLowerCase());
    const matchesSource = !fwSourceFilter || f.issuing_body === fwSourceFilter;
    const matchesSector = !fwSectorFilter || (f.sector_tags ?? []).includes(fwSectorFilter);
    const matchesYear = !fwYearFilter || String(new Date(f.last_updated).getFullYear()) === fwYearFilter;
    return matchesSearch && matchesSource && matchesSector && matchesYear;
  });

  const totalImported = importedGroups.reduce((sum, g) => sum + g.items.length, 0);

  // ── Framework detail
  const openFrameworkDetail = async (fw: GlobalFramework) => {
    setSelectedFramework(fw);
    setFwIndicators([]);
    setSelectedIndicatorIds(new Set());
    setFwIndicatorSearch("");
    setLoadingIndicators(true);
    try {
      const { indicators } = await getFrameworkDetail(fw.id, projectId);
      setFwIndicators(indicators);
    } catch {
      toast.error("Failed to load framework indicators.");
    } finally {
      setLoadingIndicators(false);
    }
  };

  const filteredFwIndicators = fwIndicators.filter(
    (i) => !fwIndicatorSearch || i.name.toLowerCase().includes(fwIndicatorSearch.toLowerCase()),
  );

  const selectableIndicators = filteredFwIndicators.filter((i) => !i.already_imported);
  const allSelected =
    selectableIndicators.length > 0 &&
    selectableIndicators.every((i) => selectedIndicatorIds.has(i.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIndicatorIds(new Set());
    } else {
      setSelectedIndicatorIds(new Set(selectableIndicators.map((i) => i.id)));
    }
  };

  const toggleIndicator = (id: string) => {
    setSelectedIndicatorIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleImportSelected = async () => {
    if (!selectedFramework || selectedIndicatorIds.size === 0 || !projectId) return;
    setImportingIndicators(true);
    try {
      const result = await importIndicators(projectId, selectedFramework.id, Array.from(selectedIndicatorIds));
      toast.success(
        `${result.imported_count} indicator${result.imported_count !== 1 ? "s" : ""} imported.` +
        (result.skipped_count > 0 ? ` (${result.skipped_count} already imported, skipped)` : ""),
      );
      // Refresh indicator statuses
      const { indicators } = await getFrameworkDetail(selectedFramework.id, projectId);
      setFwIndicators(indicators);
      setSelectedIndicatorIds(new Set());
      // Refresh framework imported_count badges
      const { items } = await listFrameworks({ project_id: projectId });
      setFrameworks(items);
      // Refresh imported tab in background
      refreshImported();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Import failed.");
    } finally {
      setImportingIndicators(false);
    }
  };

  // ── Modal helpers
  const openAddModal = () => {
    setModal({ ...MODAL_DEFAULT, open: true, mode: "add" });
    setDirtyModal(false);
  };

  const openEditModal = (ref: LibraryReference) => {
    const base: ModalState = {
      ...MODAL_DEFAULT,
      open: true,
      mode: "edit",
      id: ref.id,
      title: ref.title,
      category: ref.category,
      description: ref.description ?? "",
      citation: ref.citation ?? "",
      date: dayjs(ref.created_at).format("YYYY-MM-DD"),
    };
    if (ref.category === "benchmark_value" && ref.value) {
      const v = ref.value as BenchmarkValueContent;
      base.bv_value = String(v.value ?? "");
      base.bv_unit = v.unit ?? "%";
      base.bv_operator = v.operator ?? "";
      base.bv_source_text = v.source_text ?? "";
      base.bv_source_url = v.source_url ?? "";
    } else if (ref.category === "indicator_framework" && ref.value) {
      const raw = ref.value?.rows ?? (Array.isArray(ref.value) ? ref.value : []);
      const rows = raw.map((r: any) => ({
        name: r.indicator_name ?? r.name ?? "",
        description: r.description ?? "",
        default_value: String(r.default_value ?? ""),
      }));
      base.if_rows = rows.length > 0 ? rows : [{ name: "", description: "", default_value: "" }];
    } else if ((ref.category === "classification" || ref.category === "other") && ref.value) {
      const v = ref.value;
      base.co_text = typeof v === "string" ? v : (v?.text ?? "");
      base.co_mode = "text";
    } else if (ref.category === "lookup_table") {
      base.lt_file_name = t("modal.lookup.existingFile");
    }
    setModal(base);
    setDirtyModal(false);
  };

  const closeModal = () => {
    if (dirtyModal) {
      Modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes. Are you sure you want to close?",
        okText: "Discard",
        okType: "danger",
        cancelText: "Keep editing",
        onOk: () => setModal((m) => ({ ...m, open: false })),
      });
    } else {
      setModal((m) => ({ ...m, open: false }));
    }
  };

  const updateModal = (patch: Partial<ModalState>) => {
    setModal((m) => ({ ...m, ...patch }));
    setDirtyModal(true);
  };

  const changeCategory = (cat: ReferenceCategory) => {
    setModal((m) => ({
      ...MODAL_DEFAULT,
      open: true,
      mode: m.mode,
      id: m.id,
      title: m.title,
      description: m.description,
      citation: m.citation,
      date: m.date,
      category: cat,
    }));
  };

  const saveModal = async () => {
    if (!isModalValid(modal) || !projectId) return;
    setSavingModal(true);
    try {
      if (modal.mode === "add") {
        let valueStr: string | undefined;
        let file: File | undefined;
        if (modal.category === "lookup_table" && modal.lt_file) {
          file = modal.lt_file;
        } else if ((modal.category === "classification" || modal.category === "other") && modal.co_mode === "file" && modal.co_file) {
          file = modal.co_file;
        } else {
          const v = buildReferenceValueJSON(modal);
          if (v !== null) valueStr = JSON.stringify(v);
        }
        const newRef = await createReference(projectId, {
          title: modal.title.trim(),
          category: modal.category,
          description: modal.description.trim() || undefined,
          value: valueStr,
          file,
          citation: modal.citation.trim() || undefined,
          date: modal.date ? new Date(modal.date).toISOString() : undefined,
        });
        setReferences((prev) => [newRef, ...prev]);
        toast.success("Reference added.");
      } else {
        const valueJSON = buildReferenceValueJSON(modal);
        const updated = await updateReference(projectId, modal.id!, {
          title: modal.title.trim(),
          category: modal.category,
          description: modal.description.trim() || null,
          value: valueJSON,
          citation: modal.citation.trim() || null,
        });
        setReferences((prev) => prev.map((r) => (r.id === modal.id ? updated : r)));
        toast.success("Reference updated.");
      }
      setModal((m) => ({ ...m, open: false }));
      setDirtyModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Failed to save reference.");
    } finally {
      setSavingModal(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !projectId) return;
    setDeletingRef(true);
    try {
      await deleteReference(projectId, deleteTarget.id);
      setReferences((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Reference deleted.");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Failed to delete reference.");
    } finally {
      setDeletingRef(false);
    }
  };

  const removeImported = (id: string, title: string) => {
    Modal.confirm({
      title: "Remove from project?",
      content: `"${title}" will be removed from this project's imported references.`,
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (!projectId) return;
        try {
          await unimportReferences(projectId, [id]);
          await refreshImported();
          toast.success("Removed from project.");
        } catch {
          toast.error("Failed to remove.");
        }
      },
    });
  };

  const toggleImportedGroup = (code: string) => {
    setExpandedFrameworks((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  // ── Render: Category badge
  const renderCategoryBadge = (cat: ReferenceCategory) => {
    const def = CATEGORY_DEFS.find((d) => d.value === cat);
    return (
      <span
        className="lib-badge"
        style={{
          background: "#FCF3FF",
          color: "#4A1C96",
          borderColor: "#4A1C9630",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {def && <span style={{ display: "flex", alignItems: "center" }}>{def.icon}</span>}
        {categoryLabels[cat]}
      </span>
    );
  };

  // ── Render: Category picker (visual cards)
  const renderCategoryPicker = () => (
    <>
      <div className="lib-cat-grid">
        {categoryDefs.map((cat) => (
          <div
            key={cat.value}
            className={`lib-cat-card ${modal.category === cat.value ? "active" : ""}`}
            onClick={() => changeCategory(cat.value)}
          >
            <div className="lib-cat-card-icon" style={{ color: modal.category === cat.value ? "#4a1c96" : "#6b7280" }}>
              {cat.icon}
            </div>
            <div>
              <div className="lib-cat-card-label">{cat.label}</div>
              <div className="lib-cat-card-desc">{cat.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div
        className={`lib-cat-other ${modal.category === "other" ? "active" : ""}`}
        onClick={() => changeCategory("other")}
      >
        {t("modal.otherCard")}
      </div>
    </>
  );

  // ── Render: Conditional modal content
  const renderModalContent = () => {
    switch (modal.category) {
      case "benchmark_value":
        return (
          <div className="lib-drawer-section">
            <label className="lib-drawer-label">{t("modal.benchmark.valueLabel")} <span className="lib-required">*</span></label>
            <div className="lib-bv-row">
              <Input
                type="number"
                placeholder={t("modal.benchmark.valuePlaceholder")}
                value={modal.bv_value}
                onChange={(e) => updateModal({ bv_value: e.target.value })}
                className="lib-bv-input"
              />
              <Select
                value={modal.bv_unit}
                onChange={(v) => updateModal({ bv_unit: v })}
                options={unitOptions}
                className="lib-bv-unit"
              />
            </div>
            <Select
              value={modal.bv_operator}
              onChange={(v) => updateModal({ bv_operator: v })}
              options={operatorOptions}
              placeholder={t("modal.benchmark.operatorPlaceholder")}
              style={{ width: "100%" }}
            />
            <label className="lib-drawer-label" style={{ marginTop: 4 }}>{t("modal.benchmark.sourceLabel")}</label>
            <Input
              placeholder={t("modal.benchmark.sourceTextPlaceholder")}
              value={modal.bv_source_text}
              onChange={(e) => updateModal({ bv_source_text: e.target.value })}
              style={{ marginBottom: 6 }}
            />
            <Input
              placeholder={t("modal.benchmark.sourceUrlPlaceholder")}
              value={modal.bv_source_url}
              onChange={(e) => updateModal({ bv_source_url: e.target.value })}
            />
          </div>
        );

      case "lookup_table":
        return (
          <div className="lib-drawer-section">
            <label className="lib-drawer-label">{t("modal.lookup.fileLabel")} <span className="lib-required">*</span></label>
            <div
              className="lib-upload-area"
              onClick={() => ltFileInputRef.current?.click()}
            >
              <UploadOutlined style={{ fontSize: 22, color: "#4a1c96" }} />
              <div style={{ textAlign: "center" }}>
                <span className="lib-upload-text">
                  {modal.lt_file_name ? (
                    modal.lt_file_name
                  ) : (
                    <>
                      {t("modal.lookup.dragDropText")}{" "}
                      <span style={{ textDecoration: "underline" }}>{t("modal.lookup.clickToSelect")}</span>
                    </>
                  )}
                </span>
                {!modal.lt_file_name && (
                  <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                    {t("modal.lookup.fileHint")}
                  </div>
                )}
              </div>
            </div>
            <input
              ref={ltFileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) updateModal({ lt_file_name: f.name, lt_file: f });
                e.target.value = "";
              }}
            />
          </div>
        );

      case "indicator_framework":
        return (
          <div className="lib-drawer-section">
            <label className="lib-drawer-label">{t("modal.indicatorFramework.indicatorsLabel")} <span className="lib-required">*</span></label>
            <div className="lib-if-header-row">
              <span className="lib-if-col-label">{t("modal.indicatorFramework.nameColumn")}</span>
              <span className="lib-if-col-label">{t("modal.indicatorFramework.descriptionColumn")}</span>
              <span className="lib-if-col-label">{t("modal.indicatorFramework.defaultValueColumn")}</span>
              <span />
            </div>
            {modal.if_rows.map((row, i) => (
              <div key={i} className="lib-if-row">
                <Input
                  placeholder={t("modal.indicatorFramework.namePlaceholder")}
                  value={row.name}
                  onChange={(e) => {
                    const rows = modal.if_rows.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r);
                    updateModal({ if_rows: rows });
                  }}
                />
                <Input
                  placeholder={t("modal.indicatorFramework.descriptionPlaceholder")}
                  value={row.description ?? ""}
                  onChange={(e) => {
                    const rows = modal.if_rows.map((r, idx) => idx === i ? { ...r, description: e.target.value } : r);
                    updateModal({ if_rows: rows });
                  }}
                />
                <Input
                  placeholder={t("modal.indicatorFramework.valuePlaceholder")}
                  value={row.default_value ?? ""}
                  onChange={(e) => {
                    const rows = modal.if_rows.map((r, idx) => idx === i ? { ...r, default_value: e.target.value } : r);
                    updateModal({ if_rows: rows });
                  }}
                />
                <button
                  className="lib-if-remove-btn"
                  disabled={modal.if_rows.length === 1}
                  onClick={() => updateModal({ if_rows: modal.if_rows.filter((_, idx) => idx !== i) })}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="lib-if-add-row-btn"
              onClick={() => updateModal({ if_rows: [...modal.if_rows, { name: "", description: "", default_value: "" }] })}
            >
              <PlusOutlined /> {t("modal.indicatorFramework.addIndicator")}
            </button>
          </div>
        );

      case "classification":
      case "other":
        return (
          <div className="lib-drawer-section">
            <label className="lib-drawer-label">{t("modal.classification.contentLabel")}</label>
            <div className="lib-co-mode-toggle">
              <button
                className={`lib-co-mode-btn ${modal.co_mode === "text" ? "active" : ""}`}
                onClick={() => updateModal({ co_mode: "text" })}
              >
                {t("modal.classification.freeText")}
              </button>
              <button
                className={`lib-co-mode-btn ${modal.co_mode === "file" ? "active" : ""}`}
                onClick={() => updateModal({ co_mode: "file" })}
              >
                {t("modal.classification.fileUpload")}
              </button>
            </div>
            {modal.co_mode === "text" ? (
              <textarea
                className="lib-co-textarea"
                placeholder={t("modal.classification.textareaPlaceholder")}
                value={modal.co_text}
                onChange={(e) => updateModal({ co_text: e.target.value })}
                rows={4}
              />
            ) : (
              <div className="lib-upload-area" onClick={() => coFileInputRef.current?.click()}>
                <UploadOutlined style={{ fontSize: 22, color: "#4a1c96" }} />
                <div style={{ textAlign: "center" }}>
                  <span className="lib-upload-text">
                    {modal.co_file_name ? (
                      modal.co_file_name
                    ) : (
                      <>
                        {t("modal.classification.dragDropText")}{" "}
                        <span style={{ textDecoration: "underline" }}>{t("modal.classification.clickToSelect")}</span>
                      </>
                    )}
                  </span>
                  {!modal.co_file_name && (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                      {t("modal.classification.fileHint")}
                    </div>
                  )}
                </div>
                <input
                  ref={coFileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) updateModal({ co_file_name: f.name, co_file: f });
                    e.target.value = "";
                  }}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ── Render: Add/Edit Reference modal
  const renderModal = () => (
    <Modal
      open={modal.open}
      onCancel={closeModal}
      footer={null}
      width={520}
      centered
      destroyOnClose
      title={
        <span style={{ color: "#4a1c96", fontWeight: 700, fontSize: 16 }}>
          {modal.mode === "add" ? t("modal.addTitle") : t("modal.editTitle")}
        </span>
      }
    >
      <div className="lib-modal-body">
        <div className="lib-drawer-field">
          <label className="lib-drawer-label">{t("modal.titleLabel")} <span className="lib-required">*</span></label>
          <Input
            placeholder={t("modal.titlePlaceholder")}
            value={modal.title}
            onChange={(e) => updateModal({ title: e.target.value })}
            maxLength={200}
          />
        </div>

        <div className="lib-drawer-field">
          <label className="lib-drawer-label">
            {t("modal.descriptionLabel")}{" "}
            <span style={{ color: "#9ca3af", fontWeight: 400 }}>{t("modal.optional")}</span>
          </label>
          <Input.TextArea
            placeholder={t("modal.descriptionPlaceholder")}
            value={modal.description}
            onChange={(e) => updateModal({ description: e.target.value })}
            rows={2}
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        </div>

        <div className="lib-drawer-field">
          <label className="lib-drawer-label">{t("modal.categoryLabel")}</label>
          {renderCategoryPicker()}
        </div>

        {renderModalContent()}

        <div className="lib-drawer-field">
          <label className="lib-drawer-label">{t("modal.citationLabel")}</label>
          <Input
            placeholder={t("modal.citationPlaceholder")}
            value={modal.citation}
            onChange={(e) => updateModal({ citation: e.target.value })}
          />
        </div>

        <div className="lib-drawer-field">
          <label className="lib-drawer-label">{t("modal.dateLabel")}</label>
          <Input
            type="date"
            value={modal.date}
            onChange={(e) => updateModal({ date: e.target.value })}
          />
        </div>

        <div className="lib-modal-footer">
          <button className="lib-btn-secondary" onClick={closeModal}>{t("modal.cancel")}</button>
          <button
            className="lib-btn-primary"
            onClick={saveModal}
            disabled={!isModalValid(modal) || savingModal}
          >
            {savingModal ? <Spin size="small" /> : modal.mode === "add" ? t("modal.addReference") : t("modal.saveReference")}
          </button>
        </div>
      </div>
    </Modal>
  );

  // ── Render: My References — Reference Library sub-view
  const renderReferenceLibrary = () => (
    <>
      <div className="lib-toolbar">
        <Input
          placeholder={t("referenceLibrary.searchPlaceholder")}
          suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          value={refSearch}
          onChange={(e) => setRefSearch(e.target.value)}
          className="lib-toolbar-search"
          allowClear
        />
        <Select
          value={refCategoryFilter || undefined}
          onChange={(v) => setRefCategoryFilter(v ?? "")}
          placeholder={t("referenceLibrary.filterByCategory")}
          allowClear
          className="lib-toolbar-select"
          options={availableCategories.map((cat) => ({ value: cat, label: categoryLabels[cat] }))}
        />
        <button className="lib-btn-primary" onClick={openAddModal} style={{ marginLeft: "auto" }}>
          <PlusOutlined /> {t("referenceLibrary.addReference")}
        </button>
      </div>

      {loadingRefs ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : filteredRefs.length === 0 ? (
        <div className="lib-empty-state">
          {references.length === 0 ? (
            <>
              <div className="lib-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h4 className="lib-empty-title">{t("referenceLibrary.emptyTitle")}</h4>
              <p className="lib-empty-sub">
                {t("referenceLibrary.emptyDesc")}
              </p>
              <div className="lib-empty-actions">
                <button className="lib-btn-primary" onClick={openAddModal}>
                  <PlusOutlined /> {t("referenceLibrary.addReference")}
                </button>
              </div>
            </>
          ) : (
            <p className="lib-empty-sub">{t("referenceLibrary.noMatch")}</p>
          )}
        </div>
      ) : (
        <div className="lib-card-grid">
          {filteredRefs.map((ref) => (
            <div key={ref.id} className="lib-ref-card">
              <div className="lib-ref-card-header">
                {renderCategoryBadge(ref.category)}
                <Dropdown
                  menu={{
                    items: [
                      { key: "edit", label: t("referenceLibrary.editAction") },
                      { key: "delete", label: t("referenceLibrary.deleteAction"), danger: true },
                    ],
                    onClick: ({ key }) => {
                      if (key === "edit") openEditModal(ref);
                      if (key === "delete") setDeleteTarget({ id: ref.id, title: ref.title });
                    },
                  }}
                  trigger={["click"]}
                >
                  <button className="lib-icon-btn" onClick={(e) => e.stopPropagation()}>
                    <MoreOutlined />
                  </button>
                </Dropdown>
              </div>
              <h4 className="lib-ref-card-title">{ref.title}</h4>
              {ref.description && <p className="lib-ref-card-desc">{ref.description}</p>}
              {ref.citation && (
                <p className="lib-ref-card-citation">{t("referenceLibrary.citationPrefix", { citation: ref.citation })}</p>
              )}
              <div className="lib-ref-card-footer">
                <span className="lib-ref-card-value">{getValueSummary(ref, t)}</span>
                <span className="lib-ref-card-linked">
                  <span style={{ color: "#4a1c96" }}>•</span>{" "}
                  {t("referenceLibrary.linkedCount", { count: ref.linked_count ?? 0 })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // ── Render: My References — Imported sub-view
  const renderImported = () => {
    if (loadingImported) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (importedGroups.length === 0) {
      return (
        <div className="lib-empty-state">
          <div className="lib-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
          <h4 className="lib-empty-title">{t("imported.emptyTitle")}</h4>
          <p className="lib-empty-sub">
            {t("imported.emptyDesc")}
          </p>
          <button className="lib-btn-primary" onClick={() => setActiveTab("global-standards")}>
            {t("imported.browseGlobalStandards")}
          </button>
        </div>
      );
    }

    return (
      <div className="lib-imported-groups">
        {importedGroups.map((group) => {
          const isExpanded = expandedFrameworks.has(group.framework_code);
          const refs = group.items;
          return (
            <div key={group.framework_code} className="lib-imported-group">
              <div
                className="lib-imported-group-header"
                onClick={() => toggleImportedGroup(group.framework_code)}
              >
                <span className="lib-imported-group-name">
                  {group.framework_issuing_body} — {group.framework_name}
                </span>
                <span className="lib-imported-group-count">
                  {t("imported.indicatorCount", { count: refs.length })}
                </span>
                <span className="lib-imported-group-chevron">{isExpanded ? "▾" : "▸"}</span>
              </div>
              {isExpanded && (
                <table className="lib-imported-table">
                  <thead>
                    <tr>
                      <th>{t("imported.table.indicator")}</th>
                      <th>{t("imported.table.code")}</th>
                      <th>{t("imported.table.defaultBenchmark")}</th>
                      <th>{t("imported.table.unit")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {refs.map((ref) => {
                      const bv = ref.value as BenchmarkValueContent | null;
                      return (
                        <tr key={ref.id} className="lib-imported-row">
                          <td className="lib-imported-name">{ref.title}</td>
                          <td className="lib-imported-source-id">{ref.source_indicator_id ?? "—"}</td>
                          <td>{bv?.value !== undefined ? bv.value : "—"}</td>
                          <td>{bv?.unit ?? "—"}</td>
                          <td>
                            <div className="lib-imported-actions">
                              <button
                                className="lib-icon-btn lib-icon-btn--danger"
                                onClick={() => removeImported(ref.id, ref.title)}
                              >
                                <DeleteOutlined />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Render: My References tab
  const renderMyReferences = () => (
    <div className="lib-tab-content">
      <div className="lib-myref-header">
        <div>
          <h2 className="lib-page-title" style={{ fontSize: 16, margin: 0 }}>{t("myReferences.title")}</h2>
          <p className="lib-page-subtitle" style={{ margin: "4px 0 0" }}>
            {t("myReferences.subtitle")}
          </p>
        </div>
        <div className="lib-subview-toggle">
          <button
            className={`lib-subview-btn ${myRefView === "library" ? "active" : ""}`}
            onClick={() => setMyRefView("library")}
          >
            {t("myReferences.referenceLibraryTab")}
          </button>
          <button
            className={`lib-subview-btn ${myRefView === "imported" ? "active" : ""}`}
            onClick={() => setMyRefView("imported")}
          >
            {totalImported > 0 ? t("myReferences.importedWithCount", { count: totalImported }) : t("myReferences.imported")}
          </button>
        </div>
      </div>

      {myRefView === "library" ? renderReferenceLibrary() : renderImported()}
    </div>
  );

  // ── Render: Global Standards tab
  const renderGlobalStandards = () => {
    if (selectedFramework) {
      return (
        <div className="lib-tab-content">
          <div className="lib-fw-detail-topbar">
            <button className="lib-back-btn" onClick={() => setSelectedFramework(null)}>
              <ArrowLeftOutlined /> {t("globalStandards.detail.backButton")}
            </button>
            <div className="lib-fw-detail-actions">
              <Input
                placeholder={t("globalStandards.detail.searchIndicatorsPlaceholder")}
                suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                value={fwIndicatorSearch}
                onChange={(e) => setFwIndicatorSearch(e.target.value)}
                style={{ width: 220, borderRadius: 8, height: 36 }}
                allowClear
              />
              <button
                className="lib-btn-primary"
                disabled={selectedIndicatorIds.size === 0 || importingIndicators}
                onClick={handleImportSelected}
              >
                {importingIndicators ? (
                  <Spin size="small" />
                ) : selectedIndicatorIds.size > 0 ? (
                  t("globalStandards.detail.importSelectedWithCount", { count: selectedIndicatorIds.size })
                ) : (
                  t("globalStandards.detail.importSelected")
                )}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <h3 className="lib-fw-detail-name">{selectedFramework.name}</h3>
            <p className="lib-fw-detail-body">{selectedFramework.issuing_body}</p>
            <p className="lib-fw-detail-desc">{selectedFramework.description}</p>
            <div className="lib-fw-detail-meta">
              {(selectedFramework.sector_tags ?? []).map((s) => (
                <span key={s} className="lib-sector-tag">{s}</span>
              ))}
            </div>
          </div>

          {loadingIndicators ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : (
            <div className="lib-fw-indicator-table-wrap">
              <table className="lib-fw-indicator-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        style={{ accentColor: "#4a1c96" }}
                      />
                    </th>
                    <th>{t("globalStandards.detail.table.indicatorName")}</th>
                    <th>{t("globalStandards.detail.table.code")}</th>
                    <th>{t("globalStandards.detail.table.defaultBenchmark")}</th>
                    <th>{t("globalStandards.detail.table.unit")}</th>
                    <th>{t("globalStandards.detail.table.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFwIndicators.map((ind) => (
                    <tr
                      key={ind.id}
                      className={`lib-fw-ind-row ${ind.already_imported ? "lib-fw-ind-row--imported" : ""}`}
                      onClick={() => !ind.already_imported && toggleIndicator(ind.id)}
                    >
                      <td>
                        {!ind.already_imported && (
                          <input
                            type="checkbox"
                            className="lib-checkbox"
                            checked={selectedIndicatorIds.has(ind.id)}
                            onChange={() => toggleIndicator(ind.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ accentColor: "#4a1c96" }}
                          />
                        )}
                      </td>
                      <td className="lib-fw-ind-name">{ind.name}</td>
                      <td className="lib-fw-ind-desc">{ind.indicator_code ?? "—"}</td>
                      <td>
                        {ind.default_benchmark !== null && ind.default_benchmark !== undefined
                          ? ind.default_benchmark
                          : "—"}
                      </td>
                      <td>{ind.unit ?? "—"}</td>
                      <td>
                        {ind.already_imported ? (
                          <span className="lib-ind-status-imported">{t("globalStandards.detail.statusImported")}</span>
                        ) : (
                          <span className="lib-ind-status-available">{t("globalStandards.detail.statusAvailable")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="lib-tab-content">
        <div style={{ marginBottom: 20 }}>
          <h2 className="lib-page-title" style={{ fontSize: 16, margin: "0 0 4px" }}>{t("globalStandards.title")}</h2>
          <p className="lib-page-subtitle" style={{ margin: 0 }}>
            {t("globalStandards.subtitle")}
          </p>
        </div>

        <div className="lib-fw-toolbar">
          <Input
            placeholder={t("globalStandards.searchPlaceholder")}
            suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={fwSearch}
            onChange={(e) => setFwSearch(e.target.value)}
            className="lib-fw-toolbar-search"
            allowClear
          />
          <Select
            value={fwSourceFilter || undefined}
            onChange={(v) => setFwSourceFilter(v ?? "")}
            placeholder={t("globalStandards.filterBySource")}
            allowClear
            style={{ minWidth: 170 }}
            options={allIssuingBodies.map((b) => ({ value: b, label: b }))}
          />
          <Select
            value={fwSectorFilter || undefined}
            onChange={(v) => setFwSectorFilter(v ?? "")}
            placeholder={t("globalStandards.filterBySector")}
            allowClear
            style={{ minWidth: 160 }}
            options={allSectors.map((s) => ({ value: s, label: s }))}
          />
          <Select
            value={fwYearFilter || undefined}
            onChange={(v) => setFwYearFilter(v ?? "")}
            placeholder={t("globalStandards.filterByYear")}
            allowClear
            style={{ minWidth: 150 }}
            options={allYears.map((y) => ({ value: y, label: y }))}
          />
        </div>

        {loadingFrameworks ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <div className="lib-fw-grid">
            {filteredFrameworks.map((fw) => {
              const abbr = ISSUING_ABBR[fw.issuing_body] ?? fw.issuing_body.slice(0, 2).toUpperCase();
              const year = new Date(fw.last_updated).getFullYear();
              return (
                <div key={fw.id} className="lib-fw-card">
                  <div className="lib-fw-card-badges">
                    <span className="lib-fw-abbr-badge">{abbr}</span>
                    {fw.imported_count != null && fw.imported_count > 0 && (
                      <span className="lib-fw-imported-badge">
                        {t("globalStandards.importedBadge", { count: fw.imported_count })}
                      </span>
                    )}
                  </div>
                  <h4 className="lib-fw-card-name">{fw.name}</h4>
                  <p className="lib-fw-card-meta-line">{fw.issuing_body} · {year}</p>
                  <p className="lib-fw-card-desc">{fw.description}</p>
                  <div className="lib-fw-sectors">
                    {(fw.sector_tags ?? []).slice(0, 3).map((s) => (
                      <span key={s} className="lib-sector-tag">{s}</span>
                    ))}
                  </div>
                  <div className="lib-fw-card-footer">
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      {t("globalStandards.indicatorsCount", { count: fw.indicator_count })}
                    </span>
                    <button
                      className="lib-link-btn lib-link-btn--purple"
                      onClick={() => openFrameworkDetail(fw)}
                    >
                      {t("globalStandards.viewFramework")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Main render
  return (
    <div className="lib-page">
      <div className="lib-card">
        <div className="lib-card-header">
          <h1 className="lib-page-title">{t("pageTitle")}</h1>
        </div>

        <div className="lib-tab-bar">
          {(["my-references", "global-standards"] as const).map((tab) => {
            const labels = {
              "my-references": t("tabs.myReferences"),
              "global-standards": t("tabs.globalStandards"),
            };
            return (
              <button
                key={tab}
                className={`lib-tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab !== "global-standards") setSelectedFramework(null);
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {activeTab === "my-references" && renderMyReferences()}
        {activeTab === "global-standards" && renderGlobalStandards()}

        {renderModal()}

        <Modal
          open={!!deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onOk={confirmDelete}
          confirmLoading={deletingRef}
          okText={t("deleteConfirm.okText")}
          okType="danger"
          cancelText={t("deleteConfirm.cancelText")}
          title={t("deleteConfirm.title")}
        >
          <p>
            {t("deleteConfirm.contentPrefix")}<strong>"{deleteTarget?.title}"</strong>{t("deleteConfirm.contentSuffix")}
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default LibraryPage;
